/**
 * Profile Completion Utility
 * Calculates completion percentage and tracks required fields for KPCA members
 */

// Define all required fields and their weights
export const PROFILE_REQUIREMENTS = {
  // Basic Company Information (30% total)
  companyInfo: {
    weight: 30,
    fields: {
      companyName: { required: true, weight: 5 },
      registrationNumber: { required: true, weight: 5 },
      companyEmail: { required: true, weight: 5 },
      companyPhone: { required: true, weight: 5 },
      employees: { required: true, weight: 5 },
      address: { required: true, weight: 5 }
    }
  },
  
  // Contact Information (20% total)
  contactInfo: {
    weight: 20,
    fields: {
      physicalLocation: { required: true, weight: 5 },
      contactPerson: { required: true, weight: 5 },
      position: { required: true, weight: 5 },
      contactTelephone: { required: true, weight: 5 }
    }
  },
  
  // Required Company Documents (40% total)
  companyDocuments: {
    weight: 40,
    fields: {
      registration: { required: true, weight: 5 },
      cr12: { required: true, weight: 5 },
      kraPin: { required: true, weight: 5 },
      businessPermit: { required: true, weight: 5 },
      taxCompliance: { required: true, weight: 5 },
      nssfCompliance: { required: true, weight: 5 },
      shaCompliance: { required: true, weight: 5 }
    }
  },
  
  // Personal Documents (10% total)
  personalDocuments: {
    weight: 10,
    fields: {
      passportPhotos: { required: true, weight: 5 },
      nationalIdPassport: { required: true, weight: 5 }
    }
  }
};

/**
 * Check if a field value is considered complete
 * @param {*} value - Field value to check
 * @returns {boolean} - Whether the field is complete
 */
export const isFieldComplete = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.length > 0;
  return !!value;
};

/**
 * Calculate profile completion percentage
 * @param {Object} profileData - Complete profile data
 * @returns {Object} - Completion details
 */
export const calculateProfileCompletion = (profileData) => {
  if (!profileData) {
    return {
      totalPercentage: 0,
      isComplete: false,
      completedSections: {},
      missingSections: {},
      missingFields: []
    };
  }

  const { profile, company } = profileData;
  const completedSections = {};
  const missingSections = {};
  const missingFields = [];
  let totalScore = 0;
  let maxScore = 0;

  // Combine all data for easier access
  const allData = {
    // Company info
    companyName: company?.companyName || company?.company_name,
    registrationNumber: company?.registrationNumber || company?.company_registration_number,
    companyEmail: company?.companyEmail || company?.company_email,
    companyPhone: company?.companyPhone || company?.company_phone,
    employees: company?.numberOfEmployees || company?.employees,
    address: company?.businessAddress || company?.address,
    
    // Contact info
    physicalLocation: company?.physicalLocation || company?.physical_location,
    contactPerson: company?.contactPerson || company?.contact_person,
    position: company?.positionInCompany || company?.position,
    contactTelephone: company?.contactTelephone || company?.contact_telephone,
    
    // Documents (from certificates object or direct fields)
    registration: company?.certificates?.registration || company?.certificate_of_registration,
    cr12: company?.certificates?.cr12 || company?.cr12_certificate,
    kraPin: company?.certificates?.kraPin || company?.kra_pin_certificate,
    businessPermit: company?.certificates?.businessPermit || company?.business_permit,
    taxCompliance: company?.certificates?.taxCompliance || company?.tax_compliance_certificate,
    nssfCompliance: company?.certificates?.nssfCompliance || company?.nssf_compliance_certificate,
    shaCompliance: company?.certificates?.shaCompliance || company?.sha_compliance_certificate,
    passportPhotos: company?.certificates?.passportPhotos || company?.passport_photos,
    nationalIdPassport: company?.certificates?.nationalIdPassport || company?.national_id_passport
  };

  // Calculate completion for each section
  Object.entries(PROFILE_REQUIREMENTS).forEach(([sectionKey, section]) => {
    let sectionScore = 0;
    let sectionMaxScore = 0;
    const sectionMissingFields = [];

    Object.entries(section.fields).forEach(([fieldKey, fieldConfig]) => {
      const fieldValue = allData[fieldKey];
      const isComplete = isFieldComplete(fieldValue);
      
      sectionMaxScore += fieldConfig.weight;
      if (isComplete) {
        sectionScore += fieldConfig.weight;
      } else {
        sectionMissingFields.push({
          field: fieldKey,
          section: sectionKey,
          weight: fieldConfig.weight
        });
      }
    });

    const sectionPercentage = sectionMaxScore > 0 ? (sectionScore / sectionMaxScore) * 100 : 0;
    
    completedSections[sectionKey] = {
      score: sectionScore,
      maxScore: sectionMaxScore,
      percentage: sectionPercentage,
      isComplete: sectionPercentage === 100
    };

    if (sectionMissingFields.length > 0) {
      missingSections[sectionKey] = {
        missingFields: sectionMissingFields,
        percentage: sectionPercentage
      };
    }

    totalScore += sectionScore;
    maxScore += sectionMaxScore;
    missingFields.push(...sectionMissingFields);
  });

  const totalPercentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
  const isComplete = totalPercentage >= 100;

  return {
    totalPercentage,
    isComplete,
    completedSections,
    missingSections,
    missingFields,
    totalScore,
    maxScore
  };
};

/**
 * Get user-friendly field names
 */
export const FIELD_LABELS = {
  companyName: 'Company Name',
  registrationNumber: 'Registration Number',
  companyEmail: 'Company Email',
  companyPhone: 'Company Phone',
  employees: 'Number of Employees',
  address: 'Business Address',
  physicalLocation: 'Physical Location',
  contactPerson: 'Contact Person',
  position: 'Position in Company',
  contactTelephone: 'Contact Telephone',
  registration: 'Registration Certificate',
  cr12: 'CR12 Certificate',
  kraPin: 'KRA Pin Certificate',
  businessPermit: 'Business Permit',
  taxCompliance: 'Tax Compliance Certificate',
  nssfCompliance: 'NSSF Compliance Certificate',
  shaCompliance: 'SHA Compliance Certificate',
  passportPhotos: 'Passport Photos',
  nationalIdPassport: 'National ID/Passport Copy'
};

/**
 * Get section-friendly names
 */
export const SECTION_LABELS = {
  companyInfo: 'Company Information',
  contactInfo: 'Contact Information', 
  companyDocuments: 'Company Documents',
  personalDocuments: 'Personal Documents'
};

/**
 * Check if profile completion meets minimum threshold
 * @param {number} percentage - Completion percentage
 * @param {number} threshold - Minimum required percentage (default: 100)
 * @returns {boolean} - Whether threshold is met
 */
export const meetsCompletionThreshold = (percentage, threshold = 100) => {
  return percentage >= threshold;
};

/**
 * Get next required actions for profile completion
 * @param {Object} completionData - Result from calculateProfileCompletion
 * @returns {Array} - Array of next action items
 */
export const getNextActions = (completionData) => {
  if (!completionData || completionData.isComplete) {
    return [];
  }

  const actions = [];
  
  // Prioritize by section importance and completion percentage
  Object.entries(completionData.missingSections)
    .sort(([, a], [, b]) => a.percentage - b.percentage) // Sort by lowest completion first
    .forEach(([sectionKey, sectionData]) => {
      sectionData.missingFields.forEach(field => {
        actions.push({
          field: field.field,
          section: sectionKey,
          sectionLabel: SECTION_LABELS[sectionKey],
          fieldLabel: FIELD_LABELS[field.field],
          weight: field.weight,
          priority: field.weight >= 5 ? 'high' : 'medium'
        });
      });
    });

  return actions;
}; 